import { BIOMES, type BiomeId } from '@goodtime/shared';
import { pickLevel, tilesIn, type Rect, type Tile, type TileSet } from './tiles';
import {
  STRIP_BRIDGE,
  STRIP_GLINTS,
  STRIP_PLACES,
  STRIP_ROAD,
  STRIP_STILL,
  STRIP_WORLD_DATA,
  type Pt,
  type StripWorldData,
} from './overviewData';

/**
 * The traveller's map: one painted strip (6144x2048, the approved 2x art) the player pans sideways, snapping to each of the seven
 * worlds, with the worlds' scenes (vignettes) and lamps placed over it in code. Pure data and helpers (no Pixi); the scene is
 * scenes/OverviewScene and the camera maths overviewCamera.ts. Coordinates are master pixels of the strip.
 */

export const STRIP_W = 6144;
export const STRIP_H = 2048;
/** The strip's tiles (public/realm/v8, cut by scripts/tile-map.sh at quality 75). A 6144 wide master needs more than the realm's 20
 *  tiles of one level on a desktop, so it carries its own cap (about 40) or desktops would silently draw the level below. */
export const STRIP_TILES: TileSet = {
  base: '/realm/v8/map_files',
  width: STRIP_W,
  height: STRIP_H,
  tileSize: 512,
  top: 4,
  maxTiles: 40,
};
/** The folder of the strip's other files: vignettes/<biome>.webp, lamps/{unlit,lit}.webp, parchment.webp and fade/<n>.webp. */
export const STRIP_FILES = '/realm/v8';
/** The content band (parchment above and below it is the strip's margin), master y. */
export const STRIP_BAND: readonly [number, number] = [280, 1880];
/** The top of the scenes (the highest vignette), and the line the chosen world's panel hangs from. */
export const SCENES_TOP = 610;
export const PANEL_LINE = 1660;
/** The parchment's repeat, and the top fade's slices (each 1536 wide, 160 high, above the master). */
export const PARCHMENT_TILE = 512;
export const FADE_SLICE_W = 1536;
export const FADE_SLICES = 4;
export const FADE_H = 160;

/** The apron shows above the painting (its top edge is on screen: a fade is needed) when the strip's top edge is lower than this
 *  many pixels below the screen's top; below it likewise. A sliver is not worth the fade's bytes. */
export const APRON_SHOWS = 24;
export function apronAbove(layY: number): boolean {
  return layY > APRON_SHOWS;
}
export function apronBelow(layY: number, zoom: number, viewH: number): boolean {
  return layY + STRIP_H * zoom < viewH - APRON_SHOWS;
}

export type { Pt };

export interface StripWorld extends StripWorldData {
  /** Index into BIOMES. */
  biome: number;
  /** The world's name. */
  name: string;
  /** Where the world's name and panel hang from: under its clearing. */
  label: { x: number; y: number };
  /** The painted part's extent along x (its tap area's), master pixels: a scene counts as in view when this touches it. */
  reach: readonly [number, number];
}

/** The seven worlds, west to east along the road. */
export const STRIP_WORLDS: readonly StripWorld[] = STRIP_WORLD_DATA.map((d) => {
  const biome = Math.max(
    0,
    BIOMES.findIndex((b) => b.id === d.id),
  );
  const xs = d.tap.map((p) => p[0]);
  return {
    ...d,
    biome,
    name: BIOMES[biome]?.name ?? d.id,
    label: { x: d.stop, y: PANEL_LINE },
    reach: [Math.min(...xs), Math.max(...xs)] as const,
  };
});

/** The strip index (west to east) of a world by its BIOMES index, or -1. */
export function stripIndexOf(biome: number): number {
  return STRIP_WORLDS.findIndex((w) => w.biome === biome);
}

/** The strip index of the world a BIOMES index names, falling back to the Kingdom's (the home of a player with no slot yet). */
export function homeStrip(biome: number): number {
  const i = stripIndexOf(biome);
  return i >= 0 ? i : Math.max(0, stripIndexOf(0));
}

export function stripWorldById(id: BiomeId): StripWorld | undefined {
  return STRIP_WORLDS.find((w) => w.id === id);
}

/** The vignette's file under {@link STRIP_FILES}. */
export function vignetteUrl(w: StripWorld): string {
  return `${STRIP_FILES}/vignettes/${w.art}.webp`;
}
export const LAMP_UNLIT_URL = `${STRIP_FILES}/lamps/unlit.webp`;
export const LAMP_LIT_URL = `${STRIP_FILES}/lamps/lit.webp`;
export const PARCHMENT_URL = `${STRIP_FILES}/parchment.webp`;
export function fadeUrl(i: number): string {
  return `${STRIP_FILES}/fade/${i}.webp`;
}
/** The bottom fade's slices (the master's own bottom rows mirrored below it), the same size as the top's. */
export function fadeBottomUrl(i: number): string {
  return `${STRIP_FILES}/fadeb/${i}.webp`;
}
/** The lamp sprites' size and where their foot is in them (opaque rows 5 to 226 of 232). */
export const LAMP_SIZE = { w: 55, h: 232, footY: 227 } as const;

/** The two lamps of a world, west then east: their feet on the verge. */
export function lampFeet(w: StripWorld): readonly Pt[] {
  return w.lamps;
}

export interface Lamp {
  /** Strip index of the world they light for. */
  world: number;
  side: 'west' | 'east';
  foot: Pt;
}
export const STRIP_LAMPS: readonly Lamp[] = STRIP_WORLDS.flatMap((w, i) => [
  { world: i, side: 'west' as const, foot: w.lamps[0] },
  { world: i, side: 'east' as const, foot: w.lamps[1] },
]);

// ---- where things are ----

/** The road's centre y and width at a strip x, or null off its ends. */
export function roadAt(x: number): { y: number; w: number } | null {
  const f = (x - STRIP_ROAD.x0) / STRIP_ROAD.step;
  if (f < 0 || f > STRIP_ROAD.y.length - 1) return null;
  const i = Math.min(STRIP_ROAD.y.length - 2, Math.floor(f));
  const t = f - i;
  return {
    y: STRIP_ROAD.y[i]! * (1 - t) + STRIP_ROAD.y[i + 1]! * t,
    w: STRIP_ROAD.w[i]! * (1 - t) + STRIP_ROAD.w[i + 1]! * t,
  };
}

export function inPolygon(p: Pt, poly: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The still areas (the sea and the stream) as polygons, by name. */
export const STILL_AREAS: Readonly<Record<string, readonly Pt[]>> = STRIP_STILL;
export const BRIDGE_BOX = STRIP_BRIDGE;

/** A world's tap area: a point on its scene's painted part. */
export function onScene(w: StripWorld, p: Pt): boolean {
  return inPolygon(p, w.tap);
}

/** Pins and glints as fractions of the strip, for RealmPlaces' `positions` (by place id and secret id). */
export function stripPositions(): Record<string, { x: number; y: number }> {
  const out: Record<string, { x: number; y: number }> = {};
  for (const [id, [x, y]] of [...Object.entries(STRIP_PLACES), ...Object.entries(STRIP_GLINTS)]) {
    out[id] = { x: x / STRIP_W, y: y / STRIP_H };
  }
  return out;
}

// ---- the lamps' meaning (A: a world's pair is lit once the player has played any slot there) ----

/** The worlds whose lamps are lit now: those played (the realm view's slots), joined with those remembered on this device; offline
 *  (`played` null) the remembered set, or the world of the slot the player is on when nothing is remembered. Every number here is an
 *  index into STRIP_WORLDS (west to east: the Cove is 0, Greendale 3), never into BIOMES (where Greendale is 0): `homeStrip` makes
 *  the one for the slot the player is on. */
export function litWorlds(
  remembered: readonly number[],
  played: readonly number[] | null,
  homeStripIndex: number,
): number[] {
  const base = played ? [...remembered, ...played] : remembered.length > 0 ? [...remembered] : [homeStripIndex];
  return [...new Set(base)].sort((a, b) => a - b);
}

/** What changed since the device last saw the lamps: the worlds lit already (shown lit at once) and the newly reached (they light lamp
 *  by lamp once, then are remembered). */
export function lampMoment(
  remembered: readonly number[],
  now: readonly number[],
): { steady: number[]; fresh: number[] } {
  const had = new Set(remembered);
  return { steady: now.filter((w) => had.has(w)), fresh: now.filter((w) => !had.has(w)) };
}

/** The line a tap on a lamp says. */
export function lampLine(worldName: string, lit: boolean): string {
  return lit ? `You have played in ${worldName}: its lamps are lit.` : `Play a slot in ${worldName} to light its lamps.`;
}

/** "3 of 7 places open". */
export function placesLine(open: number, total: number): string {
  return `${open} of ${total} places open`;
}

// ---- loading ----

/** The strip's tiles to draw at a view: the base tile, then the level the zoom wants over `rect` (master pixels). */
export function tilesFor(zoom: number, dpr: number, rect: Rect): Tile[] {
  const level = pickLevel(STRIP_TILES, zoom, dpr, rect);
  return [...tilesIn(STRIP_TILES, 0, rect), ...(level > 0 ? tilesIn(STRIP_TILES, level, rect) : [])];
}

/** The world indexes whose painted part (its reach along x) touches `rect` grown by `extra` on each side (master x), and at least
 *  `share` of it (0 to 1) is inside that: the first view asks for the scenes mostly in view (a neighbour's edge peeking in waits for
 *  the prefetch), the prefetch for any that touch. */
export function scenesIn(rect: Rect, extra = 0, share = 0): number[] {
  const l = rect.x - extra;
  const r = rect.x + rect.w + extra;
  return STRIP_WORLDS.flatMap((w, i) => {
    const inside = Math.min(r, w.reach[1]) - Math.max(l, w.reach[0]);
    return inside > 0 && inside >= share * (w.reach[1] - w.reach[0]) ? [i] : [];
  });
}

/** The worlds with a lamp on screen: a lamp's post is 30 master pixels either side of its foot, and the lamp is 232 tall (so a lamp
 *  just past the edge, whose post is still in view, counts). A newly reached world lights its lamps when one is in sight, not when only
 *  the edge of its scene is (a phone at the world before it sees the next scene's edge, not its lamps, and the moment would go unseen). */
export function worldsWithLampInView(rect: Rect): number[] {
  const l = rect.x - 30;
  const r = rect.x + rect.w + 30;
  return STRIP_WORLDS.flatMap((w, i) => (w.lamps.some((p) => p[0] >= l && p[0] <= r) ? [i] : []));
}

/** The newly reached worlds waiting to light (strip indexes) that have a lamp in view now. */
export function freshInView(pending: Iterable<number>, rect: Rect): number[] {
  const inView = new Set(worldsWithLampInView(rect));
  return [...pending].filter((w) => inView.has(w));
}

/** How much of a scene must be in view for the first view to wait for it. */
export const FIRST_VIEW_SHARE = 0.35;

/** The master x range a stop's neighbours add: how far one stop each side is from this one, for prefetching tiles. */
export function stopReach(i: number): number {
  const here = STRIP_WORLDS[i]!.stop;
  const west = STRIP_WORLDS[i - 1]?.stop ?? here;
  const east = STRIP_WORLDS[i + 1]?.stop ?? here;
  return Math.max(here - west, east - here);
}

/** How far past the view tiles are kept ready along x: nothing before the first view has drawn; then one stop each side, less what
 *  would push a level over the strip's tile cap. */
export function tileMarginX(zoom: number, viewW: number, i: number, prefetch: boolean): number {
  if (!prefetch) return 0;
  const cap = STRIP_TILES.maxTiles ?? 40;
  const rows = Math.ceil(STRIP_H / STRIP_TILES.tileSize);
  const cols = Math.floor(cap / rows);
  const spare = (cols - (Math.ceil(viewW / zoom / STRIP_TILES.tileSize) + 1)) * STRIP_TILES.tileSize;
  return Math.max(0, Math.min(stopReach(i), spare / 2));
}

/** A point well inside a world's tap area (its middle, by area), where a test or a hint can tap. */
export function tapPoint(w: StripWorld): Pt {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < w.tap.length; i++) {
    const [x0, y0] = w.tap[i]!;
    const [x1, y1] = w.tap[(i + 1) % w.tap.length]!;
    const k = x0 * y1 - x1 * y0;
    a += k;
    cx += (x0 + x1) * k;
    cy += (y0 + y1) * k;
  }
  const p: Pt = a === 0 ? [w.stop, w.clearing.y] : [cx / (3 * a), cy / (3 * a)];
  return inPolygon(p, w.tap) ? p : [w.stop, w.clearing.y - 200];
}
